import { api, ApiError } from "./http";
import { getToken } from "./auth";
import { wsUrlFor } from "./config";
import type { Frame } from "./types";

export type ConnState = "idle" | "connecting" | "open" | "reconnecting" | "closed";

type FrameHandler = (f: Frame) => void;
type StateHandler = (s: ConnState, reopened: boolean) => void;

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  timer: number;
}

interface TicketReply {
  ticket: string;
  expiresIn: number;
  wsUrl?: string;
}

export interface Socket {
  send(data: string): void;
  close(): void;
  readyState: number;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onmessage: ((e: { data: unknown }) => void) | null;
  onerror: (() => void) | null;
}

export interface RealtimeDeps {
  openSocket?: (url: string) => Socket;
  fetchTicket?: () => Promise<TicketReply>;
  hasToken?: () => boolean;
}

const OPEN = 1;
const PING_EVERY_MS = 25_000;
const PONG_WITHIN_MS = 10_000;

/** The chat WebSocket: one-time ticket, request/response ops, automatic reconnect with jittered back-off, and a
 *  liveness ping so a half-dead connection (sleeping laptop, dropped wifi) is noticed and replaced. */
export class Realtime {
  private ws: Socket | null = null;
  private state: ConnState = "idle";
  private frames = new Set<FrameHandler>();
  private states = new Set<StateHandler>();
  private pending = new Map<string, Pending>();
  private seq = 0;
  private attempt = 0;
  private retryTimer: number | undefined;
  private pingTimer: number | undefined;
  private pongTimer: number | undefined;
  private wantOpen = false;
  private everOpened = false;
  private readonly deps: Required<RealtimeDeps>;

  constructor(deps: RealtimeDeps = {}) {
    this.deps = {
      openSocket: deps.openSocket ?? ((url) => new WebSocket(url) as unknown as Socket),
      fetchTicket: deps.fetchTicket ?? (() => api<TicketReply>("/api/chat/ticket", { method: "POST", retries: 1 })),
      hasToken: deps.hasToken ?? (() => !!getToken()),
    };
  }

  get connection(): ConnState {
    return this.state;
  }

  onFrame(h: FrameHandler): () => void {
    this.frames.add(h);
    return () => this.frames.delete(h);
  }

  onState(h: StateHandler): () => void {
    this.states.add(h);
    return () => this.states.delete(h);
  }

  start(): void {
    this.wantOpen = true;
    if (this.state === "idle" || this.state === "closed") void this.connect();
  }

  stop(): void {
    this.wantOpen = false;
    window.clearTimeout(this.retryTimer);
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null;
      try {
        ws.close();
      } catch {
        /* already closed */
      }
    }
    this.failAll(new Error("connection closed"));
    this.setState("closed", false);
  }

  /** Call when the network or tab comes back: skip the wait. */
  nudge(): void {
    if (!this.wantOpen) return;
    if (this.state === "reconnecting") {
      window.clearTimeout(this.retryTimer);
      void this.connect();
    } else if (this.state === "open") {
      this.ping();
    }
  }

  isOpen(): boolean {
    return this.state === "open" && this.ws?.readyState === OPEN;
  }

  request<T = unknown>(op: string, d?: unknown, timeoutMs = 10_000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!this.isOpen() || !this.ws) {
        reject(new Error("offline"));
        return;
      }
      const id = String(++this.seq);
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error("timeout"));
      }, timeoutMs);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      try {
        this.ws.send(JSON.stringify({ id, op, d }));
      } catch (e) {
        window.clearTimeout(timer);
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error("send failed"));
      }
    });
  }

  /** Fire and forget (typing indicators and the like). */
  notify(op: string, d?: unknown): void {
    if (this.isOpen() && this.ws) {
      try {
        this.ws.send(JSON.stringify({ id: String(++this.seq), op, d }));
      } catch {
        /* the reconnect logic will notice */
      }
      // the reply to a notify has no waiter: it is simply ignored
    }
  }

  private setState(s: ConnState, reopened: boolean) {
    this.state = s;
    this.states.forEach((h) => h(s, reopened));
  }

  private async connect(): Promise<void> {
    if (!this.wantOpen) return;
    if (!this.deps.hasToken()) {
      this.setState("closed", false);
      return;
    }
    this.setState(this.everOpened ? "reconnecting" : "connecting", false);
    let reply: TicketReply;
    try {
      reply = await this.deps.fetchTicket();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        this.wantOpen = false;
        this.setState("closed", false);
        return;
      }
      this.scheduleRetry();
      return;
    }
    if (!this.wantOpen) return;
    const url = `${wsUrlFor(reply.wsUrl)}${wsUrlFor(reply.wsUrl).includes("?") ? "&" : "?"}ticket=${encodeURIComponent(reply.ticket)}`;
    let ws: Socket;
    try {
      ws = this.deps.openSocket(url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onmessage = (e) => this.onMessage(e.data);
    ws.onerror = () => undefined; // onclose follows
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      this.failAll(new Error("connection lost"));
      if (this.wantOpen) this.scheduleRetry();
      else this.setState("closed", false);
    };
    // The socket is "open" for callers once the server's hello frame arrives: only then is the ticket proven good.
  }

  private scheduleRetry(): void {
    this.setState("reconnecting", false);
    this.attempt++;
    const cap = Math.min(30_000, 1000 * 2 ** Math.min(this.attempt, 5));
    const wait = Math.random() * cap + 250; // full jitter
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => void this.connect(), wait);
  }

  private onMessage(data: unknown): void {
    if (typeof data !== "string") return;
    let m: Record<string, unknown>;
    try {
      m = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return;
    }
    if (typeof m.ok === "boolean") {
      const id = typeof m.id === "string" ? m.id : "";
      const p = this.pending.get(id);
      if (p) {
        this.pending.delete(id);
        window.clearTimeout(p.timer);
        if (m.ok) p.resolve(m.d);
        else {
          const err = (m.err ?? {}) as { code?: string; msg?: string };
          p.reject(new ApiError(0, err.code ?? "error", err.msg ?? "Request failed"));
        }
      }
      this.gotPong();
      return;
    }
    const t = typeof m.t === "string" ? m.t : "";
    if (!t) return;
    if (t === "hello") {
      const reopened = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.setState("open", reopened);
      this.startPing();
    }
    this.gotPong();
    const f: Frame = { t, d: m.d };
    this.frames.forEach((h) => {
      try {
        h(f);
      } catch (e) {
        console.error("frame handler failed", t, e);
      }
    });
  }

  private startPing() {
    this.clearTimers();
    this.pingTimer = window.setInterval(() => this.ping(), PING_EVERY_MS);
  }

  private ping() {
    if (!this.isOpen()) return;
    window.clearTimeout(this.pongTimer);
    this.pongTimer = window.setTimeout(() => {
      // nothing came back: drop the socket and let the retry logic build a new one
      const ws = this.ws;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        ws.onclose?.();
      }
    }, PONG_WITHIN_MS);
    this.notify("ping");
  }

  private gotPong() {
    window.clearTimeout(this.pongTimer);
  }

  private clearTimers() {
    window.clearInterval(this.pingTimer);
    window.clearTimeout(this.pongTimer);
  }

  private failAll(e: Error) {
    this.pending.forEach((p) => {
      window.clearTimeout(p.timer);
      p.reject(e);
    });
    this.pending.clear();
  }
}

export const realtime = new Realtime();
